import React, { useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { Col, Row } from "react-bootstrap";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";

import { apiFetch, API, SUPPORTED_MARKETS, MARKETS } from "../../services/api";
import LeverEditor from "../experiments/LeverEditor";
import {
    EXPERIMENT_SURFACES,
    METRIC_BY_SURFACE,
    METRIC_LABELS,
    CONTROL_METRIC,
    DEFAULT_RULES,
    apiErrorMessage,
    applyLeversToVariants,
    configToLevers,
    experimentMarket,
    leversToConfig,
    emptyExperiment,
    validateExperimentForm,
    toApiExperiment,
} from "../../helpers/experiments";

// Modale de création / édition d'une expérience A/B.
//
// Deux variantes seulement : A = contrôle (aucun levier, c'est le rendu
// actuel) et B = variante testée (leviers en données, ou nom d'une variante
// codée côté front). Le cycle de vie (démarrer / pause / conclure) n'est PAS
// piloté ici : il passe par POST /experiment-status depuis la liste, pour
// qu'une simple correction de libellé ne puisse pas relancer un test.
//
// Ordre des blocs : le sélecteur multiple de react-select conserve l'ordre de
// SÉLECTION dans sa valeur — c'est donc l'ordre de rendu. Les blocs non
// sélectionnés ne disparaissent pas : le front complète la liste avec les
// blocs restants, dans l'ordre par défaut.

// Sources de trafic proposées au ciblage (liste ouverte : le champ accepte
// aussi une valeur saisie).
const SOURCE_SUGGESTIONS = ["google", "google-cpc", "meta", "direct", "newsletter", "website-fr", "website-es"];

const LOGGED_IN_OPTIONS = [
    { value: "any", label: "Tous les visiteurs" },
    { value: "yes", label: "Connectés seulement" },
    { value: "no", label: "Non connectés seulement" },
];

const toOption = (value, label) => ({ value, label: label || value });
const toOptions = (values) => (Array.isArray(values) ? values : []).map((v) => toOption(v));

export default function ExperimentModal({ isOpen, closeModal, selectedExperiment, onSaved }) {
    const initial = useMemo(() => {
        const source = selectedExperiment && selectedExperiment.id ? selectedExperiment : null;
        if (!source) return emptyExperiment("home");
        return {
            ...emptyExperiment(source.surface || "home"),
            ...source,
            rules: { ...DEFAULT_RULES, ...(source.rules || {}) },
            targeting: { markets: [], sources: [], devices: [], loggedIn: "any", ...(source.targeting || {}) },
            variants:
                Array.isArray(source.variants) && source.variants.length >= 2
                    ? source.variants.map((v) => ({ ...v, config: v.config || {} }))
                    : emptyExperiment(source.surface || "home").variants,
        };
    }, [selectedExperiment]);

    const [form, setForm] = useState(initial);
    const [levers, setLevers] = useState(() => configToLevers(initial.surface, initial.variants[1].config));
    const [errors, setErrors] = useState([]);
    const [saving, setSaving] = useState(false);

    const surface = form.surface;
    const isEditing = !!(selectedExperiment && selectedExperiment.id);
    const isLocked = isEditing && form.status !== "draft";
    // Marché ciblé : les catégories proposées dans les leviers sont celles de ce
    // marché (une expérience ES ne doit pas nommer des catégories FR).
    const market = experimentMarket(form);

    const patch = (changes) => setForm((current) => ({ ...current, ...changes }));
    const patchRules = (changes) => setForm((current) => ({ ...current, rules: { ...current.rules, ...changes } }));
    const patchTargeting = (changes) =>
        setForm((current) => ({ ...current, targeting: { ...current.targeting, ...changes } }));
    const patchVariant = (index, changes) =>
        setForm((current) => ({
            ...current,
            variants: current.variants.map((variant, i) => (i === index ? { ...variant, ...changes } : variant)),
        }));

    // Changer de surface remet les leviers à zéro : les clés de config ne sont
    // pas les mêmes d'une surface à l'autre, les garder produirait une config
    // que le front ignorerait silencieusement.
    const changeSurface = (value) => {
        setLevers(configToLevers(value, {}));
        patch({ surface: value, metric: { primary: METRIC_BY_SURFACE[value], control: CONTROL_METRIC } });
    };

    const submit = () => {
        const config = leversToConfig(surface, levers);
        // Seule la variante éditée (la 2e) reçoit les leviers : les éventuelles
        // variantes suivantes gardent la config qu'elles portaient déjà.
        const candidate = {
            ...form,
            variants: applyLeversToVariants(form.variants, form.variants[1].key, config),
        };
        const found = validateExperimentForm(candidate);
        if (found.length > 0) {
            setErrors(found);
            return;
        }

        setSaving(true);
        setErrors([]);
        apiFetch(API.back + "/experiment", {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(toApiExperiment(candidate)),
        })
            .then((res) => {
                // L'API valide la config depuis la phase 2 (clé inconnue,
                // couleur de CTA, libellé > 60 caractères, config > 8 Ko) : son
                // message est le seul qui dise QUOI corriger, on l'affiche tel
                // quel en tête de modale.
                if (!res.ok) {
                    return res.text().then((body) => Promise.reject(new Error(apiErrorMessage(res.status, body))));
                }
                return res.json();
            })
            .then(() => {
                setSaving(false);
                if (onSaved) onSaved();
            })
            .catch((err) => {
                console.log(err);
                setSaving(false);
                setErrors(["Enregistrement impossible : " + err.message]);
            });
    };

    return (
        <Modal show={isOpen} onHide={closeModal} size="lg" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title>{isEditing ? "Modifier l'expérience" : "Nouvelle expérience"}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {errors.length > 0 ? (
                    <div className="alert alert-danger">
                        <ul className="mb-0 pl-3">
                            {errors.map((error, index) => (
                                <li key={index}>{error}</li>
                            ))}
                        </ul>
                    </div>
                ) : null}

                <Form.Group>
                    <Form.Label>Nom de l'expérience</Form.Label>
                    <Form.Control
                        type="text"
                        value={form.name}
                        placeholder="Ex : Codes promo remontés sur la home"
                        onChange={(e) => patch({ name: e.target.value })}
                    />
                </Form.Group>

                <Form.Group>
                    <Form.Label>Hypothèse (obligatoire)</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={form.hypothesis}
                        placeholder="Ex : remonter le bloc « Codes promo » au-dessus de la mise en avant infinie augmente le taux de clic vers une fiche produit, parce que la promo est le premier motif de visite."
                        onChange={(e) => patch({ hypothesis: e.target.value })}
                    />
                    <Form.Text className="text-muted">
                        Ce que l'on attend ET pourquoi. C'est ce texte qui sera relu au moment de décider.
                    </Form.Text>
                </Form.Group>

                <Row>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Surface testée</Form.Label>
                            <Select
                                options={EXPERIMENT_SURFACES}
                                value={EXPERIMENT_SURFACES.find((s) => s.value === surface) || null}
                                isDisabled={isLocked}
                                onChange={(option) => changeSurface(option ? option.value : "home")}
                            />
                            {isLocked ? (
                                <Form.Text className="text-muted">Non modifiable : l'expérience n'est plus un brouillon.</Form.Text>
                            ) : null}
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Métrique primaire (imposée par la surface)</Form.Label>
                            <Form.Control plaintext readOnly value={METRIC_LABELS[METRIC_BY_SURFACE[surface]] || ""} />
                            <Form.Text className="text-muted">
                                Contrôle : {METRIC_LABELS[CONTROL_METRIC]} — affichée, jamais décisionnelle.
                            </Form.Text>
                        </Form.Group>
                    </Col>
                </Row>

                <hr />
                <h5>Variantes</h5>
                <Row>
                    {form.variants.slice(0, 2).map((variant, index) => (
                        <Col md={6} key={variant.key}>
                            <Form.Group>
                                <Form.Label>
                                    Variante {variant.key} {index === 0 ? "(contrôle — rendu actuel, aucun levier)" : "(testée)"}
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    value={variant.name}
                                    placeholder={index === 0 ? "Contrôle" : "Variante"}
                                    onChange={(e) => patchVariant(index, { name: e.target.value })}
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>Poids (%)</Form.Label>
                                <Form.Control
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="1"
                                    value={variant.weight}
                                    onChange={(e) => patchVariant(index, { weight: parseInt(e.target.value, 10) || 0 })}
                                />
                            </Form.Group>
                        </Col>
                    ))}
                </Row>
                <div className="text-muted small mb-3">
                    La somme des poids doit faire 100 %. Actuellement{" "}
                    {form.variants.slice(0, 2).reduce((total, v) => total + (Number(v.weight) || 0), 0)} %.
                </div>

                <hr />
                <h5>Leviers de la variante {form.variants[1].key}</h5>

                <LeverEditor
                    surface={surface}
                    levers={levers}
                    market={market}
                    onChange={(changes) => setLevers((current) => ({ ...current, ...changes }))}
                />

                <hr />
                <h5>Ciblage</h5>
                <Row>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Marchés</Form.Label>
                            <Select
                                isMulti
                                placeholder="Tous les marchés…"
                                options={SUPPORTED_MARKETS.map((m) => toOption(m, MARKETS[m].flag + " " + MARKETS[m].label))}
                                value={toOptions(form.targeting.markets)}
                                onChange={(selected) => patchTargeting({ markets: (selected || []).map((o) => o.value) })}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Sources de trafic</Form.Label>
                            <CreatableSelect
                                isMulti
                                placeholder="Toutes les sources…"
                                options={SOURCE_SUGGESTIONS.map((s) => toOption(s))}
                                value={toOptions(form.targeting.sources)}
                                onChange={(selected) => patchTargeting({ sources: (selected || []).map((o) => o.value) })}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>État de connexion</Form.Label>
                            <Select
                                options={LOGGED_IN_OPTIONS}
                                value={LOGGED_IN_OPTIONS.find((o) => o.value === (form.targeting.loggedIn || "any"))}
                                onChange={(option) => patchTargeting({ loggedIn: option ? option.value : "any" })}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Appareils</Form.Label>
                            <Select isDisabled placeholder="Tous les appareils (phase 3)" />
                            <Form.Text className="text-muted">Ciblage par appareil : phase 3.</Form.Text>
                        </Form.Group>
                    </Col>
                </Row>

                <hr />
                <h5>Garde-fous</h5>
                <Row>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Durée minimale (jours)</Form.Label>
                            <Form.Control
                                type="number"
                                min="1"
                                value={form.rules.minDays}
                                onChange={(e) => patchRules({ minDays: parseInt(e.target.value, 10) || 0 })}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Durée maximale (jours)</Form.Label>
                            <Form.Control
                                type="number"
                                min="0"
                                value={form.rules.maxDays}
                                onChange={(e) => patchRules({ maxDays: parseInt(e.target.value, 10) || 0 })}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Expositions mini par variante</Form.Label>
                            <Form.Control
                                type="number"
                                min="0"
                                value={form.rules.minExposures}
                                onChange={(e) => patchRules({ minExposures: parseInt(e.target.value, 10) || 0 })}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Seuil de perte attendue (taux)</Form.Label>
                            <Form.Control
                                type="number"
                                min="0"
                                step="0.0001"
                                value={form.rules.expectedLossThreshold}
                                onChange={(e) => patchRules({ expectedLossThreshold: Number(e.target.value) || 0 })}
                            />
                            <Form.Text className="text-muted">Défaut 0,001 (0,1 point de taux de conversion).</Form.Text>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Seuil de perte attendue en revenu (€ / visiteur)</Form.Label>
                            <Form.Control
                                type="number"
                                min="0"
                                step="0.01"
                                value={form.rules.expectedLossThresholdRevenue}
                                onChange={(e) => patchRules({ expectedLossThresholdRevenue: Number(e.target.value) || 0 })}
                            />
                            <Form.Text className="text-muted">Défaut 0,05 € — lecture de contrôle, jamais décisionnelle.</Form.Text>
                        </Form.Group>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={closeModal} disabled={saving}>
                    Annuler
                </Button>
                <Button variant="primary" onClick={submit} disabled={saving}>
                    {saving ? "Enregistrement…" : "Valider"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
